import { Link } from "react-router-dom";
import { ArrowRight, Phone, Camera } from "lucide-react";
import SEO from "@/components/SEO";
import WorkCard from "@/components/WorkCard";
import { Reveal, Stagger, LineReveal } from "@/components/Reveal";
import { BUSINESS, WORK } from "@/data/site";

export default function OurWorkPage() {
  return (
    <>
      <SEO
        title="Before & After Cleaning Gallery | Julisa's House Cleaning — Kansas City"
        description="Real before and after cleaning results from Julisa's House Cleaning — ovens, stovetops, bathrooms, baseboards and whole-home resets across Overland Park, Lenexa, Olathe and Kansas City."
      />

      <section className="relative overflow-hidden bg-gradient-to-b from-sky-200/60 via-sky-100/50 to-white pt-14 pb-12 sm:pt-20 sm:pb-16">
        <div className="pointer-events-none absolute -top-24 right-0 h-80 w-80 rounded-full bg-sky-300/40 blur-3xl" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sky-700">
            <Camera className="h-3.5 w-3.5" aria-hidden="true" /> Our Work
          </p>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
            <LineReveal lines={["Before & after,", <>in every <span className="text-sky-600 italic">detail</span></>]} />
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Real results from real cleans across the Kansas City area — no stock photos, no filters.
            Every transformation below is our own work, and it's the same level of detail we bring
            to every home and business we visit.
          </p>
        </div>
      </section>

      <section className="bg-sky-50/70 pt-14 pb-20 sm:pb-28" aria-label="Before and after cleaning gallery">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Stagger className="grid gap-5 sm:grid-cols-2">
            {WORK.map((w) => (
              <WorkCard key={w.src} work={w} />
            ))}
          </Stagger>

          <Reveal className="mt-14">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-600 to-sky-800 px-6 py-12 sm:px-12 text-center shadow-2xl shadow-sky-600/25">
              <div className="pointer-events-none absolute -top-20 -right-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
              <h2 className="relative font-display text-2xl sm:text-3xl font-semibold text-white leading-tight">
                Want results like these in your space?
              </h2>
              <p className="relative mx-auto mt-3 max-w-xl text-sm sm:text-base text-sky-100">
                Tell us about your home or business and we'll put together a free, no-pressure quote.
              </p>
              <div className="relative mt-7 flex flex-col sm:flex-row justify-center gap-3">
                <Link
                  to="/quote"
                  data-testid="gallery-cta-quote-button"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-4 text-base font-semibold text-sky-700 shadow-lg transition-all duration-200 hover:bg-sky-50 active:scale-[0.98]"
                >
                  Get a Free Quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="gallery-cta-call-button"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/10 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-all duration-200 hover:bg-white/20 active:scale-[0.98]"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" /> {BUSINESS.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
